import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { findEntityByHandle } from "../core/oda";
import { selectionService } from "../selection";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface ITextStyleOperator {
  setTextStyleByHandle(
    entityId: DrawingModule.OdDbObjectId,
    styleHandle: string,
  ): boolean;
  setTextHeight(entityId: DrawingModule.OdDbObjectId, height: number): boolean;
  viewer: DRDrawingViewer;
}

export interface SetTextStyleParams {
  handle: string;
  oldStyleHandle: string | null;
  oldHeight: number | null;
  newStyleHandle: string;
  newHeight: number | null;
}

export class SetTextStyleCommand implements ICommand {
  private operator: ITextStyleOperator;
  private handle: string;
  private oldStyleHandle: string | null;
  private oldHeight: number | null;
  private newStyleHandle: string;
  private newHeight: number | null;
  private oplogEvents: OplogEmit[] = [];

  constructor(operator: ITextStyleOperator, params: SetTextStyleParams) {
    this.operator = operator;
    this.handle = params.handle;
    this.oldStyleHandle = params.oldStyleHandle;
    this.oldHeight = params.oldHeight;
    this.newStyleHandle = params.newStyleHandle;
    this.newHeight = params.newHeight;
  }

  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.operator.viewer, [
      { handle: this.handle, changeKind: "style" },
    ]);
  }

  undo(): void {
    const { module, database } = this.operator.viewer;
    const entityId = findEntityByHandle(module, database, this.handle);
    if (entityId) {
      if (this.oldStyleHandle)
        this.operator.setTextStyleByHandle(entityId, this.oldStyleHandle);
      if (this.oldHeight !== null)
        this.operator.setTextHeight(entityId, this.oldHeight);
      deleteObj(entityId);
    }
    this.operator.viewer.update();
    selectionService.updateGrid();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    const { module, database } = this.operator.viewer;
    const entityId = findEntityByHandle(module, database, this.handle);
    if (entityId) {
      this.operator.setTextStyleByHandle(entityId, this.newStyleHandle);
      if (this.newHeight !== null)
        this.operator.setTextHeight(entityId, this.newHeight);
      deleteObj(entityId);
    }
    this.operator.viewer.update();
    selectionService.updateGrid();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {}
}
